/**
 * `data-testid`s owned by the W2 employee plan self-selection flow
 * (`/select-plans`, WP6). Keep the string values identical to the e2e contract
 * (`e2e/w2-selection/selection.ts`) — the Playwright + component specs select on
 * these. Every id is namespaced `w2-select-*` so it never collides with the
 * token PHQ flow (`w2-*`) or the contract page (`w2-contract-*`).
 */
export const W2_SELECT_TID = {
  loading: 'w2-select-loading',
  loadError: 'w2-select-load-error',
  retry: 'w2-select-retry',
  /** Employee resolved to >1 selection-eligible Self employer — section
   * Onboarding OR Active per contract D1 (§4 409 multipleEmployers). */
  multiEmployer: 'w2-select-multi-employer',
  /** Not eligible (section never opens selection — neither Onboarding nor
   * Active / not Self / not small group, §4 403). */
  ineligible: 'w2-select-ineligible',

  welcome: {
    root: 'w2-select-welcome',
    start: 'w2-select-welcome-start',
  },

  plans: {
    root: 'w2-select-plans',
    /** The category heading for the current per-plan screen. */
    category: 'w2-select-plans-category',
    /** One card per offered plan line in the active category. */
    card: 'w2-select-plan-card', // suffixed `-<planId>`
    /** Who's-covered PERSON checkbox: suffixed `-<planId>-you` or `-<planId>-dep-<i>`. */
    person: 'w2-select-plan-person',
    /** The "I waive … coverage" radio at the top of each plan. */
    waive: 'w2-select-plan-waive', // suffixed `-<planId>`
    price: 'w2-select-plan-price', // suffixed `-<planId>`
    /** The waive certification accordion + its checkbox — MEDICAL line only. */
    certifyAccordion: 'w2-select-plan-certify', // suffixed `-<planId>`
    certifyCheckbox: 'w2-select-plan-certify-checkbox', // suffixed `-<planId>`
    back: 'w2-select-plans-back',
    next: 'w2-select-plans-next',
    /** Shown when the current category still has undecided lines. */
    incomplete: 'w2-select-plans-incomplete',
    saveError: 'w2-select-plans-save-error',
    // NOTE: `elect`/`coverage` tier ids were removed with the tier-radio UI
    // (replaced by per-person checkboxes + a waive radio, client copy 2026-07-15).
  },

  /**
   * The MEDICAL/Health step is a SINGLE-CHOICE screen (restructured 2026-07-16):
   * who's-covered asked ONCE at the top (a DEPENDENT checkbox per dependent — You
   * is implicit) + one global waive; then a plan MENU where exactly one plan is
   * chosen. Ancillary categories keep the per-plan `plans.*` ids above.
   */
  health: {
    root: 'w2-select-health',
    /** The who's-covered dependent checkbox: suffixed `-dep-<i>`. */
    dep: 'w2-select-health-dep',
    /** The single global "I waive health coverage" radio. */
    waive: 'w2-select-health-waive',
    /** The global health-waive certification accordion + checkbox. */
    certifyAccordion: 'w2-select-health-certify',
    certifyCheckbox: 'w2-select-health-certify-checkbox',
    /** One chosen-plan radio per offered medical plan: suffixed `-<planId>`. */
    select: 'w2-select-health-select', // suffixed `-<planId>`
    /** The price for a plan at the current who's-covered tier: suffixed `-<planId>`. */
    price: 'w2-select-health-price', // suffixed `-<planId>`
  },

  quote: {
    root: 'w2-select-quote',
    line: 'w2-select-quote-line', // suffixed `-<planId>`
    lineWaived: 'w2-select-quote-line-waived', // suffixed `-<planId>`
    total: 'w2-select-quote-total',
    back: 'w2-select-quote-back',
    continue: 'w2-select-quote-continue',
  },

  agreement: {
    root: 'w2-select-agreement',
    /** The 7-clause TERMS section. */
    terms: 'w2-select-agreement-terms',
    signerName: 'w2-select-agreement-signer-name',
    signDate: 'w2-select-agreement-sign-date',
    attestation: 'w2-select-agreement-attestation',
    finish: 'w2-select-agreement-finish',
    back: 'w2-select-agreement-back',
    signError: 'w2-select-agreement-sign-error',
  },

  done: {
    root: 'w2-select-done',
    message: 'w2-select-done-message',
  },

  /**
   * NEW shell/chrome ids (not asserted by the original specs; added for the
   * restyle + future coverage). The bottom step bar mirrors the phase progression
   * (`stepModel`); the header exposes the quote total + greeting name.
   */
  shellPanel: 'w2-select-shell-panel',
  stepbar: {
    root: 'w2-select-stepbar',
    /** One cell per bar step: suffixed `-<i>`, carries `data-state`. */
    step: 'w2-select-stepbar-step',
  },
  header: {
    /** The quote-total figure (rendered from `Header`'s `priceOverride`). */
    total: 'w2-select-header-total',
    /** The "Hello {FirstName}" greeting element. */
    name: 'w2-select-header-name',
  },
} as const;
